<template>
  <TravelersFilterPopup
    v-if="groups.length"
    :title="title"
    is-modal-only
    :static-title="t('tour-activities.popup-title')"
    modal-class="tours-activities-modal"
    filter-key="toursActivities"
    class="filter-tours-activities"
    :is-active="isActive"
    is-full-height
    :modal-size="UiModalSize.M"
    :is-disabled-reset-button="isDisabledResetButton"
    is-footer-buttons-vertical
    :is-scroll="props.isScroll"
    :total-count="categoryStore.experiencesTmpCount"
    :is-loading="categoryStore.isExperiencesCountLoading"
    v-bind="$attrs"
    @open="applyModalStyles"
    @close="onPopupClose"
    @apply="onApply"
    @reset="categoryStore.onResetFilter(EExperienceFilterType.TOUR_ACTIVITY_SLUGS)"
  >
    <div class="popup__content">
      <div class="tour-activities-search-block">
        <SearchIcon class="tour-activities-search-icon" />
        <UiInput
          :value="searchInput"
          full-size
          :type="UiInputTypes.TEXT"
          :placeholder="t('tour-activities.inputPlaceholder')"
          :class="searchInputClass"
          @input-value="onSearchInput"
        />
        <ClearSearchIcon
          v-if="searchInput"
          class="tour-activities-search__close-icon"
          @click.stop="() => (searchInput = '')"
        />
      </div>

      <ToursActivitiesPopupContent
        :groups="groups"
        :input-value="searchInput"
      />
    </div>
  </TravelersFilterPopup>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { TravelersFilterPopup } from '@experience/travelers-filter-popup-package';
  import { UiInput, UiInputTypes } from '@experience/core-input-ui';
  import { ToursActivitiesPopupContent } from '@/entities/tour-activities';
  import { useTourActivitiesTitle } from '@/features/filter/tour-activities';
  import SearchIcon from '@/shared/ui/icon/SearchIcon.vue';
  import ClearSearchIcon from '@/shared/ui/icon/ClearSearchIcon.vue';
  import ru from '../i18n/ru.json';
  import { useI18n } from '@experience/i18n';
  import { UiModalSize } from '@experience/core-modal-ui';
  import { useToursActivitiesAsChipsGroups } from '@/features/filter/tour-activities/composables/useToursActivitiesAsChipsGroups';
  import { EExperienceFilterType } from '@/widgets/filtersBlock';
  import { useCategoryStore } from '@/shared/store';
  import { applyModalStyles, resetModalStyles } from '@/shared/lib';

  defineOptions({ inheritAttrs: false });

  const categoryStore = useCategoryStore();

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  interface Props {
    isScroll?: boolean;
  }

  const props = defineProps<Props>();

  const { title, isActive, isDisabledResetButton } = useTourActivitiesTitle();

  const groups = useToursActivitiesAsChipsGroups();

  const searchInput = ref('');
  const searchInputClass = computed(() => ['tour-activities-search', { input_icon_right: searchInput.value }]);

  const onSearchInput = (v: string | number) => (searchInput.value = `${v}`);
  const onClearSearchInput = () => (searchInput.value = '');

  const onPopupClose = () => {
    resetModalStyles();
    onClearSearchInput();
    categoryStore.onResetTmpFilter(EExperienceFilterType.TOUR_ACTIVITY_SLUGS);
  };

  const onApply = () => {
    onClearSearchInput();

    categoryStore.onApplyFilter(EExperienceFilterType.TOUR_ACTIVITY_SLUGS);
  };
</script>

<style scoped lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .ui-modal__body-content-wrapper {
    padding-top: 0 !important;
  }

  .tour-activities-search-block {
    position: relative;
    z-index: 1;
    margin-bottom: 24px;
  }

  .tour-activities-search {
    width: 100%;
    background: var(--comp-input-color-background);
  }

  .tour-activities-search:deep(input) {
    --io-padding-x-left: 44px;
    font-size: var(--global-typography-size-400) !important;
    text-overflow: ellipsis;

    .input__icon {
      top: 0;
      left: 0 !important;
    }
  }

  .tour-activities-search-icon {
    position: absolute;
    top: 14px;
    bottom: 0;
    z-index: 2;
    display: flex;
    width: 48px;
    pointer-events: none;
  }

  .tour-activities-search__close-icon {
    position: absolute;
    top: 14px;
    right: 12px;
    z-index: 99;
    cursor: pointer;
  }
</style>
<style lang="scss">
  .tours-activities-modal {
    .ui-modal__body-content-wrapper {
      padding-top: 8px;
      padding-bottom: 0;
    }

    .ui-modal-header {
      padding-bottom: 16px;
    }
  }
</style>
